import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import AnimeCard from '@/components/base/AnimeCard';
import { animeList } from '@/mocks/anime';
import NotFound from '@/pages/NotFound';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const anime = animeList.find((a) => a.id === Number(id));

  if (!anime) {
    return <NotFound />;
  }

  const relatedAnime = animeList
    .filter((a) => a.id !== anime.id && (a.category === anime.category || a.tags.some((t) => anime.tags.includes(t))))
    .slice(0, 4);

  const articleSchemaData = {
    headline: `${anime.title} - 奶泡动漫高清番剧详情`,
    description: anime.description,
    datePublished: anime.releaseDate,
    author: {
      '@type': 'Organization',
      name: '奶泡动漫',
    },
    publisher: {
      '@type': 'Organization',
      name: '奶泡动漫',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://naipadongman.com/article/${anime.id}`,
    },
  };

  return (
    <>
      <SchemaOrg type="Article" data={articleSchemaData} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '分类浏览', path: '/category' },
                  { label: anime.category, path: `/category?type=${anime.category}` },
                  { label: anime.title },
                ]}
              />
            </div>

            <article className="flex flex-col lg:flex-row gap-8 md:gap-12">
              <div className="lg:w-[320px] flex-shrink-0">
                <div className="sticky top-24">
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-5">
                    <img
                      src={anime.coverImage}
                      alt={`${anime.title} - 奶泡动漫高清番剧封面`}
                      title={`${anime.title} ${anime.tags.join(' ')} - 奶泡动漫`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                      {anime.isHot && (
                        <span className="px-3 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-lg">
                          热门
                        </span>
                      )}
                      {anime.isNew && (
                        <span className="px-3 py-1 bg-accent-500 text-background-50 text-xs font-semibold rounded-lg">
                          新番
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 p-4 bg-background-100 rounded-xl">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground-500">评分</span>
                      <span className="text-lg font-bold text-primary-500">{anime.rating}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground-500">状态</span>
                      <span className={`text-sm font-medium ${
                        anime.status === '连载中' ? 'text-accent-600' : 'text-foreground-600'
                      }`}>
                        {anime.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground-500">进度</span>
                      <span className="text-sm text-foreground-700">{anime.currentEpisode}/{anime.episodeCount}集</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground-500">更新</span>
                      <span className="text-sm text-foreground-700">{anime.updateDay}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground-500">制作</span>
                      <span className="text-sm text-foreground-700">{anime.studio}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground-500">导演</span>
                      <span className="text-sm text-foreground-700">{anime.director}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-foreground-500">播放量</span>
                      <span className="text-sm text-foreground-700">{(anime.views / 10000).toFixed(0)}万</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                  {anime.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">{anime.titleJa}</p>

                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-md">
                    {anime.category}
                  </span>
                  {anime.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-md">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="prose prose-sm max-w-none mb-8">
                  <h2 className="text-lg font-semibold text-foreground-900 mb-3">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{anime.description}</p>
                </div>

                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-foreground-900 mb-4">剧集列表</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {Array.from({ length: anime.episodeCount }, (_, i) => (
                      <button
                        key={i + 1}
                        className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors text-center whitespace-nowrap ${
                          i + 1 <= anime.currentEpisode
                            ? 'bg-primary-100 text-primary-700 hover:bg-primary-200 cursor-pointer'
                            : 'bg-background-100 text-foreground-400 cursor-not-allowed'
                        }`}
                      >
                        第{i + 1}集
                      </button>
                    ))}
                  </div>
                </div>

                {relatedAnime.length > 0 && (
                  <div>
                    <h2 className="text-lg font-semibold text-foreground-900 mb-4">相关推荐</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-5" data-product-shop>
                      {relatedAnime.map((a) => (
                        <AnimeCard key={a.id} anime={a} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </article>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}